import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Snackbar,
  TextField,
  Alert,
  Pagination,
  CircularProgress,
  Menu,
  MenuItem,
  Chip,
  Divider,
  Grid,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import {
  Description as ContentIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  AutoFixHigh as AIIcon,
  VideoCall as VideoIcon
} from '@mui/icons-material';
import AccountLayout from '../../layouts/AccountLayout';
import ContentGenerationDialog from '../../components/MicroSite/Page/ContentGenerationDialog';
import { useParams, useNavigate } from 'react-router-dom';

// TabPanel component to handle tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ai-media-tabpanel-${index}`}
      aria-labelledby={`ai-media-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function AIContentGeneration() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [isGenerateContentOpen, setIsGenerateContentOpen] = useState(false);
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    content: null
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: ''
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyContent, setCopyContent] = useState('');
  
  // Tab state - set to 2 for Content tab
  const [tabValue, setTabValue] = useState(2);

  // Fetch content on component mount and when page changes
  useEffect(() => {
    fetchContents();
  }, [accountId, page]);

  const handleTabChange = (event, newValue) => {
    if (newValue === 0) {
      // Navigate to AI Images page
      navigate(`/accounts/${accountId}/ai-media`);
      return;
    } else if (newValue === 1) {
      // Navigate to AI Videos page (which is also in the AI Media page with tab=1)
      navigate(`/accounts/${accountId}/ai-media`);
      return;
    }
    setTabValue(newValue);
  };

  const fetchContents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/content/generated?accountId=${accountId}&page=${page}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch content');
      }
      
      const data = await response.json();
      setContents(data.contents);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Error fetching content:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleContentGenerated = (newContent) => {
    setContents(prevContents => [newContent, ...prevContents]);
    setSnackbar({
      open: true,
      message: 'Content generated successfully!'
    });
  };

  const handlePreviewContent = (content) => {
    setPreviewDialog({
      open: true,
      content
    });
  };

  const handleClosePreview = () => {
    setPreviewDialog({
      open: false,
      content: null
    });
  };

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(copyContent);
    setCopyDialogOpen(false);
    setSnackbar({
      open: true,
      message: 'Content copied to clipboard!'
    });
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) return;
    
    try {
      const response = await fetch(`/api/content/generated/${selectedItem.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete content');
      }

      // Refresh the list
      fetchContents();
      
      setSnackbar({
        open: true,
        message: 'Content deleted successfully!'
      });
    } catch (error) {
      console.error('Delete error:', error);
      setError(error.message);
    }
    
    handleMenuClose();
  };

  const getContentTypeLabel = (type) => {
    const types = {
      'text': 'Text',
      'html': 'HTML',
      'markdown': 'Markdown',
      'blog': 'Blog Post',
      'product': 'Product',
      'seo': 'SEO'
    };
    return types[type] || type;
  };

  const truncateText = (text, maxLength = 200) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <AccountLayout>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="AI media tabs"
            centered
          >
            <Tab label="AI Images" id="ai-media-tab-0" aria-controls="ai-media-tabpanel-0" />
            <Tab label="AI Videos" id="ai-media-tab-1" aria-controls="ai-media-tabpanel-1" />
            <Tab label="AI Content" id="ai-media-tab-2" aria-controls="ai-media-tabpanel-2" />
          </Tabs>
        </Box>
        
        {/* Content Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              AI Generated Content
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<ContentIcon />}
              onClick={() => setIsGenerateContentOpen(true)}
            >
              Generate New Content
            </Button>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : contents.length === 0 ? (
            <Alert severity="info">No content generated yet.</Alert>
          ) : (
            <>
              <Grid container spacing={3}>
                {contents.map((content) => (
                  <Grid item xs={12} md={6} key={content.id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="h6" component="div" noWrap sx={{ maxWidth: '80%' }}>
                            {content.title}
                          </Typography>
                          <Chip 
                            label={getContentTypeLabel(content.contentType)} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </Box>
                        
                        <Typography variant="caption" color="text.secondary" display="block">
                          Created: {new Date(content.createdAt).toLocaleDateString()}
                        </Typography>
                        
                        <Divider sx={{ my: 1 }} />
                        
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            mt: 1, 
                            height: '100px', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {truncateText(content.content)}
                        </Typography>
                        
                        {content.wordCount && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            Word count: {content.wordCount}
                          </Typography>
                        )}
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          startIcon={<ViewIcon />}
                          onClick={() => handlePreviewContent(content)}
                        >
                          View
                        </Button>
                        <IconButton 
                          aria-label="more options"
                          onClick={(e) => handleMenuOpen(e, content)}
                        >
                          <MoreIcon />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={(event, value) => setPage(value)} 
                  color="primary" 
                />
              </Box>
            </>
          )}
        </TabPanel>
      </Box>
      
      {/* Menu for options */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          setCopyDialogOpen(true);
          setCopyContent(selectedItem?.content || '');
          handleMenuClose();
        }}>
          <CopyIcon fontSize="small" sx={{ mr: 1 }} />
          Copy Content
        </MenuItem>
        <MenuItem onClick={handleDeleteItem} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
      
      {/* Dialog for copying content */}
      <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Copy Content</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={copyContent}
            onChange={(e) => setCopyContent(e.target.value)}
            margin="dense"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCopyToClipboard} color="primary">
            Copy to Clipboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Content preview dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {previewDialog.content && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{previewDialog.content.title}</Typography>
              <IconButton onClick={handleClosePreview} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {previewDialog.content && (
            <>
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label={getContentTypeLabel(previewDialog.content.contentType)} 
                  size="small" 
                  color="primary" 
                />
                <Chip 
                  label={`Created: ${new Date(previewDialog.content.createdAt).toLocaleString()}`} 
                  size="small" 
                  variant="outlined" 
                />
                {previewDialog.content.wordCount && (
                  <Chip 
                    label={`${previewDialog.content.wordCount} words`} 
                    size="small" 
                    variant="outlined" 
                  />
                )}
              </Box>
              
              <Typography variant="subtitle1" fontWeight="bold">Prompt:</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="body2">{previewDialog.content.prompt}</Typography>
              </Paper>
              
              <Typography variant="subtitle1" fontWeight="bold">Content:</Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {previewDialog.content.content}
                </Typography>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            startIcon={<CopyIcon />}
            onClick={() => {
              navigator.clipboard.writeText(previewDialog.content?.content || '');
              setSnackbar({
                open: true,
                message: 'Content copied to clipboard!'
              });
            }}
          >
            Copy Content
          </Button>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* Generation Dialog */}
      <ContentGenerationDialog
        open={isGenerateContentOpen}
        onClose={() => setIsGenerateContentOpen(false)}
        onContentGenerated={handleContentGenerated}
        accountId={accountId}
      />
    </AccountLayout>
  );
}

export default AIContentGeneration; 